interface Props {
  buckets: { label: string; value: number; highlight?: boolean; title?: string }[];
  height?: number;
  /** Smallest drawn bar in px, so an empty bucket still shows (design histogram: 4). */
  minBar?: number;
  /** `dense` = Student 360 `.cbars`: 3px gaps and 3px/1px bar corners (default: 6px gaps, 7px/3px corners). */
  variant?: 'default' | 'dense';
  /** Text alternative for the whole chart (the bars carry only tooltips). */
  label?: string;
  /**
   * Exam report `.bars`: the count sits above each bar (mono 11, muted) and bars are pixel-sized,
   * the tallest `barMax` px, with `minBar` as the floor. `height` is the row's fixed height.
   */
  showValues?: boolean;
  barMax?: number;
}
export default function ColumnBars({ buckets, height = 180, minBar = 0, variant = 'default', label, showValues = false, barMax = 150 }: Props) {
  const dense = variant === 'dense';
  const max = Math.max(1, ...buckets.map((b) => b.value));
  const showLabels = buckets.some((b) => b.label !== '');
  if (showValues) {
    return (
      <div role={label ? 'img' : undefined} aria-label={label}>
        <div className="flex items-end gap-1.5" style={{ height }}>
          {buckets.map((b, i) => (
            <div key={`${i}-${b.label}`} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
              <span className="font-mono text-[11px] font-medium text-muted">{b.value}</span>
              <div title={b.title ?? `${b.label}: ${b.value}`}
                className={`w-full flex-none rounded-t-[7px] rounded-b-[3px] ${b.highlight ? 'bg-accent' : 'bg-slate-ink'}`}
                style={{ height: Math.max(minBar, Math.round((b.value / max) * barMax)) }} />
            </div>
          ))}
        </div>
        {showLabels && <div className="mt-2.5 flex gap-1.5">
          {buckets.map((b, i) => <span key={`${i}-${b.label}`} className="flex-1 text-center font-mono text-[11px] text-muted">{b.label}</span>)}
        </div>}
      </div>
    );
  }
  return (
    <div role={label ? 'img' : undefined} aria-label={label}>
      <div className={`flex items-end ${dense ? 'gap-[3px]' : 'gap-1.5'}`} style={{ height }}>
        {buckets.map((b, i) => (
          <div key={`${i}-${b.label}`} title={b.title ?? `${b.label}: ${b.value}`}
            className={`flex-1 ${dense ? 'rounded-t-[3px] rounded-b-[1px]' : 'rounded-t-[7px] rounded-b-[3px]'} ${b.highlight ? 'bg-accent' : 'bg-slate'}`}
            style={{ height: `${Math.round((b.value / max) * 100)}%`, ...(minBar ? { minHeight: minBar } : {}) }} />
        ))}
      </div>
      {showLabels && <div className="mt-2.5 flex gap-1.5">
        {buckets.map((b, i) => <span key={`${i}-${b.label}`} className="flex-1 text-center font-mono text-[11px] text-muted">{b.label}</span>)}
      </div>}
    </div>
  );
}
